package com.milta.feature.route.data.remote

import com.milta.core.network.ApiException
import com.milta.core.network.Endpoints
import com.milta.core.network.OptimizationQuotaExceededException
import com.milta.core.network.baseUrl
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.route.data.remote.dto.AddDeliveriesToRouteRequest
import com.milta.feature.route.data.remote.dto.AddDeliveriesToRouteResultDto
import com.milta.feature.route.data.remote.dto.AddressesToFixErrorDto
import com.milta.feature.route.data.remote.dto.toDomain
import com.milta.feature.route.data.remote.dto.CompleteRouteRequest
import com.milta.feature.route.data.remote.dto.CreateDeliveryDto
import com.milta.feature.route.data.remote.dto.CreateOwnRouteRequest
import com.milta.feature.route.data.remote.dto.CreateRouteBatchRequest
import com.milta.feature.route.data.remote.dto.CreateRouteRequest
import com.milta.feature.route.data.remote.dto.CreateRouteResultDto
import com.milta.feature.route.data.remote.dto.DeliveryDto
import com.milta.feature.route.data.remote.dto.DeliveryProofDto
import com.milta.feature.route.data.remote.dto.DispatchRouteBatchResultDto
import com.milta.feature.route.data.remote.dto.DispatchRouteRequest
import com.milta.feature.route.data.remote.dto.ImportRouteFileResponse
import com.milta.feature.route.data.remote.dto.ImportedDeliveryDto
import com.milta.feature.route.data.remote.dto.PagedResultDto
import com.milta.feature.route.data.remote.dto.ReoptimizeInPlaceRequest
import com.milta.feature.route.data.remote.dto.ReoptimizeRouteRequest
import com.milta.feature.route.data.remote.dto.ReoptimizeRouteResultDto
import com.milta.feature.route.data.remote.dto.RouteBatchResultDto
import com.milta.feature.route.data.remote.dto.RouteDispatchResultDto
import com.milta.feature.route.data.remote.dto.RouteDto
import com.milta.feature.route.data.remote.dto.RouteHistoryItemDto
import com.milta.feature.route.data.remote.dto.RouteOptimizationResultDto
import com.milta.feature.route.data.remote.dto.RouteProofsResponseDto
import com.milta.feature.route.data.remote.dto.RouteSummaryDto
import com.milta.feature.route.data.remote.dto.StartRouteRequestDto
import com.milta.feature.route.data.remote.dto.TeamRoutesOverviewDto
import com.milta.feature.route.data.remote.dto.UpdateDeliveryAddressRequest
import com.milta.feature.route.domain.entity.AddressesToFixException
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess

class RouteApi(private val client: HttpClient) {

    /**
     * GET /api/v1/routes — Manager team dashboard (MIL-38). Replaces the former flat
     * `List<RouteDto>` shape previously returned by this exact endpoint (now dead: no backend
     * action returns that shape anymore) — kept as the same method name would be misleading, so
     * callers use this one instead of a removed `getRoutes()`.
     */
    suspend fun getTeamRoutesOverview(date: String? = null): TeamRoutesOverviewDto =
        client.get("$baseUrl/${Endpoints.routes}") {
            if (date != null) parameter("date", date)
        }.decodeOrThrow()

    /** GET /api/v1/routes/{id} — route detail, Driver (own route) or Enterprise (own tenant) (MIL-38). */
    suspend fun getRouteById(id: String): RouteDto =
        client.get("$baseUrl/${Endpoints.route(id)}").decodeOrThrow()

    /**
     * GET /api/v1/routes/history — MIL-52, paginated route history for the web dashboard's
     * "Historique des tournées" (Screen A). [from]/[to] are `yyyy-MM-dd`; omitting both defaults to
     * the last 8 rolling days server-side. The backend rejects (400 `error.invalid_date`) any `from`
     * older than `today-7`.
     */
    suspend fun getRouteHistory(
        from: String?,
        to: String?,
        driverId: String?,
        search: String?,
        page: Int,
        pageSize: Int,
    ): PagedResultDto<RouteHistoryItemDto> =
        client.get("$baseUrl/${Endpoints.routeHistory}") {
            if (from != null) parameter("from", from)
            if (to != null) parameter("to", to)
            if (driverId != null) parameter("driverId", driverId)
            if (search != null) parameter("search", search)
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.decodeOrThrow()

    /** GET /api/v1/routes/{routeId}/proofs — MIL-52, paginated proofs of one route for the web
     * dashboard's "Preuves de la tournée" (Screen B). */
    suspend fun getRouteProofs(routeId: String, search: String?, page: Int, pageSize: Int): RouteProofsResponseDto =
        client.get("$baseUrl/${Endpoints.routeProofs(routeId)}") {
            if (search != null) parameter("search", search)
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.decodeOrThrow()

    /** [routeId] pins the exact route to return, omitted (default "most recent") otherwise. */
    suspend fun getMyRoute(routeId: String? = null): RouteSummaryDto =
        client.get("$baseUrl/${Endpoints.routeMe}") {
            if (routeId != null) parameter("routeId", routeId)
        }.decodeOrThrow()

    /**
     * GET /api/v1/routes/me/days — the driver's routes for a given day (Passé/Aujourd'hui/Demain
     * navigation). Defaults to today server-side when [date] is null. Empty list, not 404, when
     * nothing is planned that day. Distinct endpoint from [getMyRoute] above — see
     * [com.milta.feature.route.domain.repository.RouteRepository.getMyRoutes] for why.
     */
    suspend fun getMyRoutes(date: String? = null): List<RouteSummaryDto> =
        client.get("$baseUrl/${Endpoints.routeMe}/days") {
            if (date != null) parameter("date", date)
        }.decodeOrThrow()

    /** POST /api/v1/routes/me — MIL-33 "Créer / Optimiser ma tournée" (driver self-service, no team picker). */
    suspend fun createOwnRoute(
        deliveries: List<CreateDeliveryDto>,
        date: String? = null,
        latitude: Double? = null,
        longitude: Double? = null,
    ): CreateRouteResultDto =
        client.post("$baseUrl/${Endpoints.routeMe}") {
            contentType(ContentType.Application.Json)
            setBody(CreateOwnRouteRequest(deliveries, date, latitude, longitude))
        }.decodeOrThrow()

    /** MIL-55 — [loadingDiscrepancyNote] optional (écran 07b, "Signaler un écart"). */
    suspend fun startRoute(id: String, loadingDiscrepancyNote: String? = null): RouteSummaryDto =
        client.patch("$baseUrl/${Endpoints.routeStart(id)}") {
            contentType(ContentType.Application.Json)
            setBody(StartRouteRequestDto(loadingDiscrepancyNote))
        }.decodeOrThrow()

    /** DELETE /api/v1/routes/{routeId}/deliveries/{deliveryId} — remove a stop (MIL-55, écran 07b). */
    suspend fun removeDeliveryFromRoute(routeId: String, deliveryId: String) {
        val response = client.delete("$baseUrl/${Endpoints.routeDelivery(routeId, deliveryId)}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }

    /** PUT /api/v1/routes/{routeId}/deliveries/{deliveryId}/address — MIL-99, the route's creator
     * corrects an unlocated stop before sending/start; returns the updated stop. */
    suspend fun updateDeliveryAddress(routeId: String, deliveryId: String, request: UpdateDeliveryAddressRequest): DeliveryDto =
        client.put("$baseUrl/${Endpoints.routeDeliveryAddress(routeId, deliveryId)}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.decodeOrThrow()

    /** POST /api/v1/routes/{id}/complete — driver-initiated end of route (MIL-36). */
    suspend fun completeRoute(id: String, confirmed: Boolean, reason: String? = null): RouteSummaryDto =
        client.post("$baseUrl/${Endpoints.routeComplete(id)}") {
            contentType(ContentType.Application.Json)
            setBody(CompleteRouteRequest(confirmed, reason))
        }.decodeOrThrow()

    /**
     * POST /api/v1/routes/{id}/reoptimize-in-place — re-orders the existing stops, no new stop
     * added (MIL-34). [latitude]/[longitude] (DOM-05) are the driver's current GPS position,
     * forwarded as an optional, more accurate depot; omitting them (both `null`) keeps the exact
     * pre-DOM-05 behavior.
     */
    suspend fun reoptimizeInPlace(id: String, latitude: Double? = null, longitude: Double? = null): RouteSummaryDto =
        client.post("$baseUrl/${Endpoints.routeReoptimizeInPlace(id)}") {
            contentType(ContentType.Application.Json)
            setBody(ReoptimizeInPlaceRequest(latitude, longitude))
        }.decodeOrThrow()

    /** POST /api/v1/routes/import — uploads a `.xlsx` and returns its parsed preview (DOM-02). */
    suspend fun importRouteFile(fileBytes: ByteArray, fileName: String): List<ImportedDeliveryDto> =
        client.submitFormWithBinaryData(
            url = "$baseUrl/${Endpoints.routeImport}",
            formData = formData {
                append(
                    key = "file",
                    value = fileBytes,
                    headers = Headers.build {
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ).decodeOrThrow<ImportRouteFileResponse>().deliveries

    /** POST /api/v1/routes — creates a route for a driver with its initial deliveries (DOM-02). */
    suspend fun createRoute(
        driverAccountId: String,
        deliveries: List<CreateDeliveryDto>,
        date: String? = null,
        loadingAddressId: String? = null,
    ): CreateRouteResultDto =
        client.post("$baseUrl/${Endpoints.routes}") {
            contentType(ContentType.Application.Json)
            setBody(CreateRouteRequest(driverAccountId, deliveries, date, loadingAddressId))
        }.decodeOrThrow()

    /** POST /api/v1/routes/{id}/optimize (DOM-02). */
    suspend fun optimizeRoute(routeId: String): RouteOptimizationResultDto =
        client.post("$baseUrl/${Endpoints.routeOptimize(routeId)}").decodeOrThrow()

    /**
     * POST /api/v1/routes/{id}/regeocode (MIL-45) — manually re-runs geocoding without a full
     * RoutaR optimization. Open to the owning driver (solo route) or a manager of the route's
     * enterprise. The response body (counts/per-delivery status) isn't needed here: the caller
     * simply reloads the route detail afterward to pick up the refreshed statuses.
     */
    suspend fun regeocodeRoute(routeId: String) {
        val response = client.post("$baseUrl/${Endpoints.routeRegeocode(routeId)}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }

    /** POST /api/v1/routes/{routeId}/reoptimize — adds a manual stop AND re-orders the whole route (MIL-35). */
    suspend fun reoptimizeRoute(routeId: String, request: ReoptimizeRouteRequest): ReoptimizeRouteResultDto =
        client.post("$baseUrl/${Endpoints.routeReoptimize(routeId)}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.decodeOrThrow()

    /** POST /api/v1/routes/{id}/dispatch (DOM-02). */
    suspend fun dispatchRoute(routeId: String, skipOptimization: Boolean?): RouteDispatchResultDto =
        client.post("$baseUrl/${Endpoints.routeDispatch(routeId)}") {
            contentType(ContentType.Application.Json)
            setBody(DispatchRouteRequest(skipOptimization))
        }.decodeOrThrow()

    /** POST /api/v1/routes/{id}/deliveries — bulk add onto an already-dispatched route (DOM-02). */
    suspend fun addDeliveriesToRoute(routeId: String, deliveries: List<CreateDeliveryDto>): AddDeliveriesToRouteResultDto =
        client.post("$baseUrl/${Endpoints.routeDeliveries(routeId)}") {
            contentType(ContentType.Application.Json)
            setBody(AddDeliveriesToRouteRequest(deliveries))
        }.decodeOrThrow()

    /** POST /api/v1/route-batches — splits a pooled delivery list across several drivers (MIL-39). */
    suspend fun createRouteBatch(request: CreateRouteBatchRequest): RouteBatchResultDto =
        client.post("$baseUrl/${Endpoints.routeBatches}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.decodeOrThrow()

    /** POST /api/v1/route-batches/{id}/reoptimize (MIL-39). */
    suspend fun reoptimizeRouteBatch(batchId: String): RouteBatchResultDto =
        client.post("$baseUrl/${Endpoints.routeBatchReoptimize(batchId)}").decodeOrThrow()

    /** POST /api/v1/route-batches/{id}/dispatch (MIL-39). */
    suspend fun dispatchRouteBatch(batchId: String): DispatchRouteBatchResultDto =
        client.post("$baseUrl/${Endpoints.routeBatchDispatch(batchId)}").decodeOrThrow()

    /**
     * POST /api/v1/routes/{routeId}/deliveries/{deliveryId}/validate — multipart: mandatory
     * `photo` file part + `status`/`comment`/`occurredAt` plain form fields (MIL-41; `occurredAt`
     * added by MIL-43 — offline capture instant, replayed as-is once the queue is synced. Null for
     * a normal online submit: the server then defaults `completedAt` to "now", which is correct
     * since there is no capture/send delay in that path).
     */
    suspend fun validateDelivery(
        routeId: String,
        deliveryId: String,
        photoBytes: ByteArray?,
        photoFileName: String?,
        status: String,
        comment: String?,
        occurredAt: String? = null,
    ): DeliveryProofDto =
        client.submitFormWithBinaryData(
            url = "$baseUrl/${Endpoints.routeDeliveryValidate(routeId, deliveryId)}",
            formData = formData {
                // Retour 27/10 (point 3) — no "photo" part at all when the driver's own route
                // made it optional and none was taken.
                if (photoBytes != null && photoFileName != null) {
                    append(
                        key = "photo",
                        value = photoBytes,
                        headers = Headers.build {
                            append(HttpHeaders.ContentDisposition, "filename=\"$photoFileName\"")
                        },
                    )
                }
                append("status", status)
                if (comment != null) append("comment", comment)
                if (occurredAt != null) append("occurredAt", occurredAt)
            },
        ).decodeOrThrow()

    /**
     * DELETE /api/v1/routes/{id} — logical delete, driver-initiated (MIL-65). 409
     * `error.conflict.route_already_started` when the route is no longer Scheduled.
     */
    /** DELETE /api/v1/route-batches/{id} — abandons a batch not sent yet (retour 26/10, R8): 204,
     * 404 unknown/other enterprise, 409 `error.route_batch_already_dispatched`. */
    suspend fun deleteRouteBatch(batchId: String) {
        val response = client.delete("$baseUrl/${Endpoints.routeBatch(batchId)}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }

    suspend fun deleteRoute(id: String) {
        val response = client.delete("$baseUrl/${Endpoints.route(id)}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }

    private suspend inline fun <reified T> HttpResponse.decodeOrThrow(): T {
        if (!status.isSuccess()) {
            val apiError = runCatching { body<ApiError>() }.getOrNull()
            // MIL-97 — keeps the quota extensions (reset date) the screens show instead of a plain 403.
            if (apiError?.code == OptimizationQuotaExceededException.CODE) {
                throw OptimizationQuotaExceededException(apiError.limit, apiError.remaining, apiError.resetAt)
            }
            // Keeps the refused deliveries (index + motif) so the screens can mark them "À corriger".
            // The body is read a second time (Ktor keeps it); unreadable = list unknown (null).
            if (apiError?.code == AddressesToFixException.CODE) {
                val toFix = runCatching { body<AddressesToFixErrorDto>() }.getOrNull()?.deliveries
                throw AddressesToFixException(toFix?.map { it.toDomain() })
            }
            val message = apiError?.code ?: "error_${status.value}"
            throw ApiException(status.value, message)
        }
        return body()
    }
}
